import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import type { AuthorityUiMetadata } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { getAuthorityAttributes } from '@atlassian/rovo-platform-ui-components/authoritative-answer-attribution/authoritative-answer-panel-shown-event';

import { filterCommonAttributes } from './analytics-helpers';
import type { commonAttributesType } from './types';

/**
 * Dedicated builder for clicks on a source or expert inside the SAIN
 * authoritative-answer panel.
 *
 * It intentionally does NOT extend the shared `onAIAnswerSourceClick` util (used
 * by the regular sources list) so that authoritative-only concerns never leak
 * onto non-authoritative source clicks. It fires the same `aiAnswerSourceLink
 * clicked` event name, reusing `filterCommonAttributes` for the same UGC-safe
 * sanitization, and adds:
 *  - `clickOrigin: 'authoritativeAnswerPanel'` — separates these clicks from the
 *    regular sources-list clicks that share the event name.
 *  - `linkType` — whether a source or an expert was clicked.
 *  - non-UGC authority attributes derived from the panel's metadata.
 */
export const onAuthoritativeSourceClick = ({
	linkType,
	authorityUiMetadata,
	...props
}: commonAttributesType & {
	linkType: 'source' | 'expert';
	authorityUiMetadata?: AuthorityUiMetadata | null;
}): AnalyticsEventPayload => {
	return {
		action: 'clicked',
		actionSubject: 'aiAnswerSourceLink',
		source: props.source,
		eventType: 'ui',
		// Intentionally non-UGC only: no query/answerString nonPrivacySafeAttributes.
		attributes: {
			...filterCommonAttributes(props),
			clickOrigin: 'authoritativeAnswerPanel',
			linkType,
			...getAuthorityAttributes(authorityUiMetadata),
		},
	};
};
